import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FormProvider,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import { toString } from 'lodash';

import { BackButton, Modal } from '@/components';
import { useSnackbar } from '@/modules/core/hooks';
import { CreatedProviderDto, CreateProviderDto } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import useAddProviderForm, { AddProviderForm, AddProviderFormField } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useAddProviderForm';

import AddProviderModalForm from './AddProviderModalForm';

interface Props {
  onSetVisible: (value: boolean) => void;
  createProvider: (createProviderDto: CreateProviderDto) => Promise<CreatedProviderDto>;
  handleChangeProvider: (providerId?: string) => void;
}

const AddProviderModal: React.FC<Props> = ({ onSetVisible, createProvider, handleChangeProvider }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { methods } = useAddProviderForm();
  const [isLoading, setIsLoading] = useState(false);
  const { pushProvider } = useDocumentOcrStore();

  const handleCloseModal = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleCreateProvider = useCallback(async (formValues: AddProviderForm) => {
    try {
      setIsLoading(true);
      const createdProvider = await createProvider({
        facility_name: formValues[AddProviderFormField.providerName],
      });
      if (!createdProvider?.id) {
        return;
      }
      handleChangeProvider(toString(createdProvider?.id));
      setIsLoading(false);
      onSetVisible(false);
      handleAddPermanentSnackbar({
        text: `${createdProvider?.facility_name} added.`,
        closeIcon: true,
        autoRemove: true,
      });
      pushProvider({
        id: toString(createdProvider?.id),
        name: createdProvider?.facility_name,
        isSuggested: false,
        address: {
          city: createdProvider?.addresses?.[0]?.city,
          line1: createdProvider?.addresses?.[0]?.line1,
          line2: createdProvider?.addresses?.[0]?.line2 || undefined,
          zipcode: createdProvider?.addresses?.[0]?.zipcode,
          state: {
            id: toString(createdProvider?.addresses?.[0]?.state?.id),
            code: createdProvider?.addresses?.[0]?.state?.code,
            name: createdProvider?.addresses?.[0]?.state?.name,
          },
        },
      });
    } catch {
      setIsLoading(false);
    }
  }, [createProvider, onSetVisible, handleAddPermanentSnackbar, handleChangeProvider, pushProvider]);

  return (
    <Modal
      testId="add-provider"
      visible
      onSetVisible={onSetVisible}
      isCloseButtonHidden={isMobile}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box>
          <BackButton
            testId="add-provider-popup"
            onClick={handleCloseModal}
            title={t('Add New Provider/ Merchant')}
          />
        </Box>
      ) : (
        <Text size="2xl" textAlign="center" weight={700}>
          {t('Add New Provider/ Merchant')}
        </Text>
      )}

      <Box
        data-testId="add-provider-form-wrapper_modal"
        width={{ max: 'none' }}
        margin={{ top: isMobile ? 'spacing16' : 'spacing24', horizontal: isMobile ? '-14px' : undefined }}
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
        round="moduleRound"
      >
        <FormProvider {...methods}>
          <AddProviderModalForm />
        </FormProvider>
      </Box>

      <Box
        margin={{ top: isMobile ? 'auto' : 'spacing24' }}
        direction="row"
        justify="end"
        gap="spacing12"
      >
        <Box flex={isMobile}>
          <AppButton
            testId="add-provider-modal_cancel"
            onClick={handleCloseModal}
            buttonType="secondary"
            width={isMobile ? '100%' : '160px'}
          >
            {t('Cancel')}
          </AppButton>
        </Box>
        <Box flex={isMobile}>
          <AppButton
            testId="add-provider-modal_submit"
            onClick={methods.handleSubmit(handleCreateProvider)}
            width={isMobile ? '100%' : '160px'}
            disabled={isLoading}
          >
            {isLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddProviderModal;
